import React, { useEffect, useRef, useState } from "react";
import { MediaVideo } from "./WorkMedia";

// One crop of a real export, placed on a project stage (see data/homepage.js
// for the placement format and home.css for how the variables are used).

// Matches the 640 breakpoint where the stage switches arrangement in home.css.
const WIDE_QUERY = "(min-width: 640px)";

export function useWideStage() {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(WIDE_QUERY);
    const update = () => setWide(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return wide;
}

const layerVars = (prefix, placement, size) => {
  const [cx, cy, cw, ch] = placement.crop;
  const [x, y, w] = placement.at;
  return {
    [`--${prefix}x`]: x,
    [`--${prefix}y`]: y,
    [`--${prefix}w`]: w,
    [`--${prefix}cx`]: cx,
    [`--${prefix}cy`]: cy,
    [`--${prefix}cw`]: cw,
    [`--${prefix}ch`]: ch,
    [`--${prefix}iw`]: size[0],
    [`--${prefix}ih`]: size[1],
  };
};

// The photograph behind a project's screens, cropped to the stage. It is
// atmosphere only: if it fails, the stage colour shows instead.
export function VisualBackdrop({ backdrop, priority = false }) {
  const [failed, setFailed] = useState(false);
  if (!backdrop || failed) return null;
  const position = backdrop.position || "50% 50%";
  return (
    <img
      className="pf-visual__backdrop"
      src={backdrop.src}
      width={backdrop.size[0]}
      height={backdrop.size[1]}
      alt=""
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      style={{
        "--pf-backdrop-position": position,
        "--pf-backdrop-position-narrow": backdrop.narrowPosition || position,
        "--pf-backdrop-zoom": backdrop.zoom || 1,
      }}
      onError={() => setFailed(true)}
    />
  );
}

// `onLoad` also runs for an image that was already complete when it
// mounted, so a cached screen still counts as ready; callers treat repeat
// calls as one.
//
// A layer with a `video` (an extension-less /videos path) still loads its
// image, the clip's poster, which is what readiness waits for. The clip is
// laid over it only while `motion` is on, so a gallery mounts one clip at a
// time and reduced motion, tiles and errors keep the still.
function VisualLayer({ layer, priority, onError, onLoad, motion = false, className = "" }) {
  const imageRef = useRef(null);
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  useEffect(() => {
    const image = imageRef.current;
    if (image && image.complete && image.naturalWidth > 0 && onLoadRef.current) {
      onLoadRef.current();
    }
  }, []);

  const { wide } = layer;
  const narrow = layer.narrow === undefined ? wide : layer.narrow;
  const classes = ["pf-visual__layer"];
  if (wide && wide.card) classes.push("pf-visual__layer--card");
  if (narrow && narrow.card) classes.push("pf-visual__layer--card-narrow");
  if (wide && wide.phone) classes.push("pf-visual__layer--phone");
  if (narrow && narrow.phone) classes.push("pf-visual__layer--phone-narrow");
  if (wide && wide.browser) classes.push("pf-visual__layer--browser");
  if (wide && wide.browser === "dark") classes.push("pf-visual__layer--browser-dark");
  if (narrow && narrow.front) classes.push("pf-visual__layer--front-narrow");
  if (layer.island) classes.push("pf-visual__layer--island");
  if (wide && wide.dim) classes.push("pf-visual__layer--dim");
  if (narrow && narrow.dim) classes.push("pf-visual__layer--dim-narrow");
  if (!narrow) classes.push("pf-visual__layer--wide-only");
  if (!wide) classes.push("pf-visual__layer--narrow-only");
  if (className) classes.push(className);

  return (
    <span
      className={classes.join(" ")}
      style={{
        ...(wide ? layerVars("w", wide, layer.size) : {}),
        ...(narrow ? layerVars("n", narrow, layer.size) : {}),
      }}
    >
      {wide && wide.browser ? (
        <span className="pf-visual__chrome">
          <i />
          <i />
          <i />
          <b />
        </span>
      ) : null}
      <img
        ref={imageRef}
        src={layer.src}
        width={layer.size[0]}
        height={layer.size[1]}
        alt=""
        loading={priority ? "eager" : "lazy"}
        fetchpriority={priority ? "high" : undefined}
        decoding="async"
        onError={onError}
        onLoad={onLoad}
      />
      {layer.video && motion ? (
        <MediaVideo src={layer.video} poster={layer.src} aria-hidden="true" />
      ) : null}
    </span>
  );
}

export default VisualLayer;
